import { CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';
import { ConnectionPositionPair, Overlay, OverlayConfig, OverlayPositionBuilder, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { PortalModule, TemplatePortal } from '@angular/cdk/portal';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    Renderer2,
    signal,
    TemplateRef,
    untracked,
    viewChild,
    viewChildren,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { OffClickModule } from '@df/module-off-click/off-click.module';
import { TimeoutService } from '@df/ng/timeout.service';
import { DragService } from '@df/ui/drag/drag.service';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { getState } from '@ngrx/signals';
import { merge } from 'rxjs/internal/observable/merge';
import { NCmsHotspots } from '../cms-hotspots.interface';
import { CmsHotspotsStore } from '../cms-hotspots.store';
import { CmsHotspotsProductComponent } from '../product/hotspots-product.component';

/**
 * Wrap images with `cms-hotspots-wrapper` component.
 * @see `cms-hero-banner` component for example of usage.
 *
 * @example
 * ```html
 * <cms-hotspots-wrapper [component]="component">
 *   <img [dfImage]="component.image"
 *        [ratio]="component.ratio || 'ratio-4-3'"
 *        fill>
 * </cms-hotspots-wrapper>
 * ```
 *
 * Extend interface of wrapped component by `NCmsHotspots.IHotspots`
 *
 * @example
 * ```ts
 * export interface IComponentData extends NCmsHotspots.IHotspots, NICmsContentComponent.IData {
 * ```
 *
 * Add the hotspots fields to the component configuration
 *
 * @example
 * ```ts
 * export const CMS_COMPONENT_HERO_BANNER_CONFIG = <Omit<NICmsContentComponent.IMeta<NICmsContentComponent.IComponent>, 'type'>>{
 *   id: NCmsHeroBanner.ID,
 *   name: 'Hero Banner',
 *   tags: ['hero', 'banner', 'homepage'],
 *   fields: [
 * ```
 */
@Component({
    selector: 'cms-hotspots-wrapper, [cmsHotspotsWrapper]',
    templateUrl: './hotspots-wrapper.component.html',
    styleUrl: './hotspots-wrapper.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        PortalModule,
        DragDropModule,
        OffClickModule,
        CmsHotspotsProductComponent
    ],
    providers: [CmsHotspotsStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(mouseenter)': 'handleMouseenter()',
        '(mouseover)': 'handleMouseenter()',
        '(mouseleave)': 'handleMouseleave()'
    }
})
export class CmsHotspotsWrapperComponent {
    /** Component that edits */
    component = input.required<NCmsHotspots.IHotspots>();

    /** Use only click to open modal */
    useClick = input<boolean>(false);

    /** Use ESC key to close modal */
    closeOnEscKey = input<boolean>(false);

    /** Toggle (open/close) modal on click */
    toggleModal = input<boolean>(false);

    isHover = signal<boolean>(false);

    targetUrl = input<string>('');

    readonly visibility = NCmsHotspots.EVisibility;

    /** Hotspot template. */
    private _hotspotTemplate = viewChild.required<TemplateRef<any>>('hotspotTemplate');
    private _hotspotsRef = viewChildren<ElementRef<HTMLDivElement>>('hotspotsRef');
    private _isDragging = false;
    private router = inject(Router);

    protected portal?: TemplatePortal<TemplateRef<any>>;
    protected closingTimeoutId: any;
    protected openedOrigin?: HTMLElement;

    protected dragService = inject(DragService);
    protected overlay = inject(Overlay);
    protected overlayPositionBuilder = inject(OverlayPositionBuilder);
    protected app = inject(App);
    protected renderer = inject(Renderer2);
    protected elementRef = inject(ElementRef);
    protected viewContainerRef = inject(ViewContainerRef);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected timeoutService = inject(TimeoutService);
    protected iCmsDataChangeService = inject(ICmsDataChangeService);
    protected document = inject(DOCUMENT);
    protected destroyRef = inject(DestroyRef);
    readonly store = inject(CmsHotspotsStore);

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(): void {
        if (this.closeOnEscKey()) {
            this.close();
        }
    }

    constructor() {
        this.iCmsDataChangeService.afterChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
            if (
                (event.changeType === NICmsDataChange.EChangeType.change ||
                    event.changeType === NICmsDataChange.EChangeType.add ||
                    event.changeType === NICmsDataChange.EChangeType.delete) &&
                this.cmsObservedData.some(path => event.valuePath.includes(path))
            ) {
                this.detectChanges();
            }
        });

        // hack for detect changes in signals store
        effect(() => {
            const _state = getState(this.store);

            this.detectChanges();
        });

        effect(() => {
            const _isHover = this.isHover();

            untracked(() => {
                this.detectChanges();
            });
        });

        effect(() => {
            const component = this.component();

            untracked(() => {
                if (component && component.hotspots) {
                    const ids: number[] = [];

                    component.hotspots.forEach(hotspot => {
                        if (hotspot.product) {
                            ids.push(hotspot.product);
                        }
                    });

                    this.app.resources.product.loadAllIds(ids, 1).finally(() => this.detectChanges());
                }
            });
        });
    }

    get cmsObservedData(): string[] {
        const keys = this.component ? Object.keys(this.component) : [];

        return ['data', ...keys];
    }

    get client(): Client {
        return this.app.client;
    }

    get canEdit(): boolean {
        return this.client.isCms;
    }

    get isOpen(): boolean {
        return !!this.overlayRef;
    }

    get overlayRef(): OverlayRef | undefined {
        return this.store.overlayRef();
    }

    set overlayRef(value: OverlayRef | undefined) {
        this.store.updateOverlayRef(value);
    }

    isExists(hotspot: NCmsHotspots.IHotspot) {
        if (hotspot.product) {
            const model = this.app.resources.product.getExisting(hotspot.product);

            return !!model;
        }

        return true;
    }

    close(): void {
        if (this.app.isPlatformBrowser) {
            this.overlayRef?.dispose();
            this.overlayRef = undefined;

            this.renderer.removeClass(this.elementRef.nativeElement, 'is-open');

            if (this.openedOrigin) {
                this.renderer.removeClass(this.openedOrigin, 'is-open');

                this.openedOrigin = undefined;
            } else {
                this._hotspotsRef().forEach((hotspotRef: ElementRef) => {
                    this.renderer.removeClass(hotspotRef.nativeElement, 'is-open');
                });
            }

            this.detectChanges();
        }
    }

    handleDragStarted(): void {
        this._isDragging = true;

        if (this.overlayRef) {
            this.overlayRef.detach();
        }
    }

    handleDragMoved(): void {
        this._isDragging = true;

        if (this.overlayRef && this.overlayRef.hasAttached()) {
            this.overlayRef.detach();
        }
    }

    handleDragEnded(event: CdkDragEnd, hotspot: NCmsHotspots.IHotspot): void {
        this._isDragging = false;

        if (this.canEdit) {
            const { height, width } = this.elementRef.nativeElement.getBoundingClientRect();
            const distanceTop = event.distance.y;
            const distanceLeft = event.distance.x;
            const top = (100 / height) * distanceTop;
            const left = (100 / width) * distanceLeft;
            const hotspotTop = hotspot.top || 0;
            const hotspotLeft = hotspot.left || 0;

            const topValue = Number(hotspotTop + top).toFixed(2);
            const leftValue = Number(hotspotLeft + left).toFixed(2);

            this.app.iCmsAdminDataScopeService.applyChange({
                changeType: NICmsDataChange.EChangeType.change,
                scopeData: hotspot,
                key: 'top',
                value: Number(topValue)
            });

            this.app.iCmsAdminDataScopeService.applyChange({
                changeType: NICmsDataChange.EChangeType.change,
                scopeData: hotspot,
                key: 'left',
                value: Number(leftValue)
            });
        }

        this.renderer.setStyle(event.source.element.nativeElement, 'transform', `translate3d(0,0,0)`);

        event.source._dragRef.reset();

        if (this.overlayRef && !this.overlayRef.hasAttached()) {
            this.overlayRef.attach(this.portal);
            this.overlayRef.updatePosition();
        }
    }

    handleMouseenter(): void {
        this.clearClosingTimeoutId();

        this.isHover.set(true);
    }

    handleMouseleave(): void {
        if (!this.canEdit) {
            this.closingTimeoutId = this.timeoutService.setTimeout(() => {
                this.close();
                this.isHover.set(false);
            }, 200);
        }
    }

    async handleHotspotEnter(origin: HTMLElement, hotspot: NCmsHotspots.IHotspot, index: number): Promise<void> {
        if (this.app.client.isCms && !this.dragService.disabled) {
            this.dragService.disabled = true;
        }

        if (hotspot && hotspot.product) {
            await this.app.resources.product.load(hotspot.product, 1);
            const model = this.app.resources.product.getExisting(hotspot.product);

            if (model) {
                hotspot.productModel = model;

                if (this.toggleModal() && this.openedOrigin === origin) {
                    this.close();
                } else {
                    this.open(origin, hotspot, index);
                }
            }
        } else {
            this.open(origin, hotspot, index);
        }
    }

    handleHotspotLeave(): void {
        if (this.app.client.isCms) {
            this.dragService.disabled = false;
        }
    }

    onClick(event: PointerEvent, origin: HTMLElement, hotspot: NCmsHotspots.IHotspot, index: number) {
        event.preventDefault(); // for regular links
        (<any>event).prevented = true; // hacky thing for optHref
        this.handleHotspotEnter(origin, hotspot, index);
    }

    clearClosingTimeoutId(): void {
        if (this.closingTimeoutId) {
            clearTimeout(this.closingTimeoutId);
        }
    }

    navigateToUrl(event: MouseEvent) {
        const target = event.target as HTMLElement;

        if (target.closest('.hotspot') || target.closest('.hotspot-content-wrapper') || !this.targetUrl().length) {
          return;
        }

        this.router.navigate([this.targetUrl()]);
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    protected async open(origin: HTMLElement, hotspot: NCmsHotspots.IHotspot, index: number): Promise<void> {
        if (this._isDragging) {
            return;
        }

        if (hotspot && hotspot.product) {
            await this.app.resources.product.load(hotspot.product, 1);
            const model = this.app.resources.product.getExisting(hotspot.product);

            if (model) {
                hotspot.productModel = model;
            } else {
                return;
            }
        }

        this.close();

        const overlayRef = this.createOverlay(origin, hotspot, index);

        if (!overlayRef.hasAttached()) {
            overlayRef.attach(this.portal);
            overlayRef.updatePosition();
        }

        merge(
            this.app.meta.routerEventsSync,
            this.app.client.obs.scroll,
            this.app.client.obs.resize,
            this.app.events.on('cmsScroll'),
            this.app.events.on('cmsResize')
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.close());

        this.detectChanges();
    }

    protected createOverlay(origin: HTMLElement, hotspot: NCmsHotspots.IHotspot, index: number): OverlayRef {
        if (!this.overlayRef) {
            this.portal = new TemplatePortal(this._hotspotTemplate(), this.viewContainerRef, {
                $implicit: hotspot,
                index
            });

            const overlayConfig = new OverlayConfig({
                positionStrategy: this.getOverlayPosition(origin, hotspot),
                scrollStrategy: this.overlay.scrollStrategies.close(),
                width: '400px',
                height: '200px'
            });

            this.openedOrigin = origin;

            this.renderer.addClass(this.openedOrigin, 'is-open');
            this.renderer.addClass(this.elementRef.nativeElement, 'is-open');

            this.overlayRef = this.overlay.create(overlayConfig);
        }

        return this.overlayRef;
    }

    protected getPositions(origin: HTMLElement, hotspot: NCmsHotspots.IHotspot): ConnectionPositionPair[] {
        const width = 350;
        const parentRect = origin.parentElement!.getBoundingClientRect();
        const hotspotTop = parentRect.height * ((hotspot.top || 0) / 100);
        const hotspotLeft = parentRect.width * ((hotspot.left || 0) / 100);
        // fix cdk position on mobile phones
        let offsetY = 0;

        if (this.app.isPlatformBrowser) {
            offsetY = document.documentElement.clientHeight - window.innerHeight;
        }

        if (this.app.client.isS) {
            return [
                {
                    originX: 'center',
                    originY: 'top',
                    overlayX: 'center',
                    overlayY: 'top',
                    offsetY: 50,
                    panelClass: 'is-centered-s'
                },
                {
                    originX: 'center',
                    originY: 'bottom',
                    overlayX: 'center',
                    overlayY: 'bottom',
                    offsetY: 80,
                    panelClass: 'is-centered-s'
                }
            ];
        }

        if (parentRect.width - hotspotLeft < width && hotspotLeft > width) {
            return [
                {
                    originX: 'start',
                    originY: 'center',
                    overlayX: 'end',
                    overlayY: 'center',
                    panelClass: 'is-center-start'
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'end',
                    overlayY: 'top',
                    panelClass: 'is-top-center'
                },
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'bottom',
                    panelClass: 'is-bottom-center'
                }
            ];
        }

        return [
            {
                originX: 'center',
                originY: 'top',
                overlayX: 'center',
                overlayY: 'bottom',
                offsetY,
                offsetX: 140,
                panelClass: 'is-top-center'
            },
            {
                originX: 'center',
                originY: 'bottom',
                overlayX: 'center',
                overlayY: 'top',
                offsetY,
                offsetX: 140,
                panelClass: 'is-bottom-center'
            },
            {
                originX: 'start',
                originY: 'center',
                overlayX: 'end',
                overlayY: 'center',
                offsetY,
                offsetX: 140,
                panelClass: 'is-center-start'
            },
            {
                originX: 'end',
                originY: 'center',
                overlayX: 'start',
                overlayY: 'center',
                offsetY,
                offsetX: 140,
                panelClass: 'is-center-end'
            }
        ];
    }

    protected getOverlayPosition(origin: HTMLElement, hotspot: NCmsHotspots.IHotspot): PositionStrategy {

        return this.overlayPositionBuilder
            .flexibleConnectedTo(origin)
            .withPositions(this.getPositions(origin, hotspot))
            .withPush(false)
            .withFlexibleDimensions(false);
    }
}
